import React, { useState, useEffect } from 'react';
import toolApprovalsService from '../services/toolApprovalsService';
import './ToolApprovals.css';

/**
 * ToolApprovals Component - Manages tool approval settings
 * 
 * Features:
 * - View all discovered tools and their approval status
 * - Configure tools as: auto_approve, require_approval, or always_deny
 * - New tools default to require_approval
 */
function ToolApprovals({ onClose, availableTools = [] }) {
  const [toolApprovals, setToolApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadToolApprovals();
  }, []);

  const loadToolApprovals = async () => {
    setLoading(true);
    setError('');
    try {
      const approvals = await toolApprovalsService.getToolApprovals();
      setToolApprovals(approvals);
    } catch (err) {
      setError('Failed to load tool approvals: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Merge available tools with saved approvals
  const getMergedTools = () => {
    const approvalMap = new Map(toolApprovals.map(a => [a.toolName, a]));
    const merged = [];

    // Add all available tools (from MCP gateways)
    availableTools.forEach(tool => {
      const saved = approvalMap.get(tool.name);
      merged.push({
        toolName: tool.name,
        description: tool.description || saved?.description || '',
        approvalStatus: saved?.approvalStatus || 'require_approval',
        updatedAt: saved?.updatedAt,
        isDiscovered: true,
      });
      approvalMap.delete(tool.name);
    });

    // Add any saved approvals for tools not currently available
    approvalMap.forEach((approval, toolName) => {
      merged.push({
        toolName,
        description: approval.description || '',
        approvalStatus: approval.approvalStatus,
        updatedAt: approval.updatedAt,
        isDiscovered: false,
      });
    });

    return merged.sort((a, b) => a.toolName.localeCompare(b.toolName));
  };

  const handleApprovalChange = async (toolName, newStatus, description = '') => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      await toolApprovalsService.putToolApproval(toolName, newStatus, description);
      setSuccessMessage(`Updated "${toolName}" to ${formatStatus(newStatus)}`);
      await loadToolApprovals();
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('toolApprovalsUpdated'));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to update tool approval: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApproval = async (toolName) => {
    if (!window.confirm(`Reset "${toolName}" to default (Require Approval)?`)) {
      return;
    }
    
    setSaving(true);
    setError('');
    try {
      await toolApprovalsService.deleteToolApproval(toolName);
      setSuccessMessage(`Reset "${toolName}" to default`);
      await loadToolApprovals();
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('toolApprovalsUpdated'));
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to reset tool approval: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkApprove = async () => {
    if (!window.confirm('Auto-approve all discovered tools? This will allow them to execute without confirmation.')) {
      return;
    }
    
    setSaving(true);
    setError('');
    try {
      const updates = getMergedTools()
        .filter(t => t.isDiscovered && t.approvalStatus !== 'auto_approve')
        .map(t => ({ toolName: t.toolName, approvalStatus: 'auto_approve', description: t.description }));
      
      await toolApprovalsService.batchUpdateToolApprovals(updates);
      setSuccessMessage(`Auto-approved ${updates.length} tools`);
      await loadToolApprovals();
      
      window.dispatchEvent(new CustomEvent('toolApprovalsUpdated'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to bulk approve: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkRequireApproval = async () => {
    if (!window.confirm('Set all discovered tools to Require Approval? This is the default secure setting.')) {
      return;
    }
    
    setSaving(true);
    setError('');
    try {
      const updates = getMergedTools()
        .filter(t => t.isDiscovered && t.approvalStatus !== 'require_approval')
        .map(t => ({ toolName: t.toolName, approvalStatus: 'require_approval', description: t.description }));
      
      await toolApprovalsService.batchUpdateToolApprovals(updates);
      setSuccessMessage(`Set ${updates.length} tools to Require Approval`);
      await loadToolApprovals();
      
      window.dispatchEvent(new CustomEvent('toolApprovalsUpdated'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to bulk update: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case 'auto_approve': return 'Auto Approve';
      case 'require_approval': return 'Require Approval';
      case 'always_deny': return 'Always Deny';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'auto_approve': return '✅';
      case 'require_approval': return '⚠️';
      case 'always_deny': return '🚫';
      default: return '❓';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'auto_approve': return 'status-auto-approve';
      case 'require_approval': return 'status-require-approval';
      case 'always_deny': return 'status-always-deny';
      default: return '';
    }
  };

  const mergedTools = getMergedTools();
  const discoveredCount = mergedTools.filter(t => t.isDiscovered).length;
  const autoApproveCount = mergedTools.filter(t => t.approvalStatus === 'auto_approve').length;
  const requireApprovalCount = mergedTools.filter(t => t.approvalStatus === 'require_approval').length;
  const alwaysDenyCount = mergedTools.filter(t => t.approvalStatus === 'always_deny').length;

  return (
    <div className="tool-approvals-overlay" onClick={onClose}>
      <div className="tool-approvals-panel" onClick={(e) => e.stopPropagation()}>
        <div className="tool-approvals-header">
          <h3>🔐 Tool Approval Settings</h3>
          <button className="close-panel-button" onClick={onClose}>×</button>
        </div>

        <div className="tool-approvals-content">
          {/* Stats Bar */}
          <div className="tool-approvals-stats">
            <div className="stat-item">
              <span className="stat-value">{discoveredCount}</span>
              <span className="stat-label">Discovered</span>
            </div>
            <div className="stat-item status-auto-approve">
              <span className="stat-value">{autoApproveCount}</span>
              <span className="stat-label">Auto</span>
            </div>
            <div className="stat-item status-require-approval">
              <span className="stat-value">{requireApprovalCount}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item status-always-deny">
              <span className="stat-value">{alwaysDenyCount}</span>
              <span className="stat-label">Denied</span>
            </div>
          </div>

          {/* Bulk Actions */}
          <div className="tool-approvals-actions">
            <button 
              onClick={handleBulkApprove} 
              disabled={saving || discoveredCount === 0}
              className="bulk-action-button approve-all"
            >
              ✅ Auto-Approve All
            </button>
            <button 
              onClick={handleBulkRequireApproval} 
              disabled={saving || discoveredCount === 0}
              className="bulk-action-button require-all"
            >
              ⚠️ Require All
            </button>
            <button 
              onClick={loadToolApprovals} 
              disabled={loading}
              className="bulk-action-button refresh"
            >
              🔄 Refresh
            </button>
          </div>

          {/* Messages */}
          {error && <div className="tool-approvals-error">{error}</div>}
          {successMessage && <div className="tool-approvals-success">{successMessage}</div>}

          {/* Info Box */}
          <div className="tool-approvals-info">
            <p>
              <strong>New tools default to "Require Approval"</strong> for security.
              Configure which tools can run automatically.
            </p>
          </div>

          {/* Tools List */}
          {loading ? (
            <div className="tool-approvals-loading">⏳ Loading tool approvals...</div>
          ) : mergedTools.length === 0 ? (
            <div className="tool-approvals-empty">
              <p>📭 No tools discovered yet</p>
              <p>Connect to MCP servers to discover available tools.</p>
            </div>
          ) : (
            <div className="tool-approvals-list">
              {mergedTools.map(tool => (
                <div 
                  key={tool.toolName} 
                  className={`tool-approval-card ${getStatusClass(tool.approvalStatus)} ${!tool.isDiscovered ? 'unavailable' : ''}`}
                >
                  <div className="tool-approval-header">
                    <div className="tool-approval-name">
                      {getStatusIcon(tool.approvalStatus)} {tool.toolName}
                    </div>
                    {!tool.isDiscovered && (
                      <span className="tool-unavailable-badge">Not Available</span>
                    )}
                  </div>
                  
                  {tool.description && (
                    <div className="tool-approval-description">{tool.description}</div>
                  )}
                  
                  <div className="tool-approval-controls">
                    <select
                      value={tool.approvalStatus}
                      onChange={(e) => handleApprovalChange(tool.toolName, e.target.value, tool.description)}
                      disabled={saving}
                      className={`approval-select ${getStatusClass(tool.approvalStatus)}`}
                    >
                      <option value="auto_approve">✅ Auto Approve</option>
                      <option value="require_approval">⚠️ Require Approval</option>
                      <option value="always_deny">🚫 Always Deny</option>
                    </select>
                    
                    {tool.updatedAt && (
                      <button
                        onClick={() => handleDeleteApproval(tool.toolName)}
                        disabled={saving}
                        className="reset-button"
                        title="Reset to default"
                      >
                        ↩️
                      </button>
                    )}
                  </div>

                  {tool.updatedAt && (
                    <div className="tool-approval-updated">
                      Last updated: {new Date(tool.updatedAt).toLocaleString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ToolApprovals;
